import React from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { cn } from 'lib/utils';
import CarrierSectionHeader from 'tailwind-components/CarrierSectionHeader/CarrierSectionHeader';
import Separator from 'tailwind-components/Separator/Separator';
import {
  getCarrierParentSectionMeta,
  getCarriersInParentSection
} from 'helpers/carriers/carrierSectionIconLegends';

const groupedCarriersShape = propTypes.arrayOf(
  propTypes.shape({
    parentGroupName: propTypes.string.isRequired,
    carriersWithoutSubgroup: propTypes.arrayOf(propTypes.object).isRequired,
    subgroups: propTypes.arrayOf(
      propTypes.shape({
        groupName: propTypes.string.isRequired,
        carriers: propTypes.arrayOf(propTypes.object).isRequired
      })
    )
  })
);

/**
 * Shared layout for dynamic carrier parent groups: section header (title,
 * optional subtitle, icon legends) + main grid + optional subgroups.
 */
export default function GroupedCarrierSections({
  groupedCarriers = [],
  subtitle,
  wrapperClassName,
  sectionClassName = 'flex flex-col gap-4',
  sectionHeaderClassName,
  filterLegendsForGrid = true,
  renderHeaderChildren,
  renderMainGrid,
  renderSubgroupGrid
}) {
  const user = useSelector(state => state.user.zohoData);

  if (!groupedCarriers.length) return null;

  return (
    <div className={wrapperClassName}>
      {groupedCarriers.map(
        ({ parentGroupName, carriersWithoutSubgroup, subgroups }) => {
          const sectionCarriers = getCarriersInParentSection({
            carriersWithoutSubgroup,
            subgroups
          });
          const { legends, showUplineDisabled } = getCarrierParentSectionMeta(
            sectionCarriers,
            user,
            { filterForGrid: filterLegendsForGrid }
          );

          return (
            <section
              key={parentGroupName}
              className={cn(sectionClassName)}
            >
              {parentGroupName ? (
                <CarrierSectionHeader
                  title={parentGroupName}
                  subtitle={subtitle}
                  legends={legends}
                  showUplineDisabled={showUplineDisabled}
                  className={sectionHeaderClassName}
                >
                  {renderHeaderChildren?.(parentGroupName)}
                </CarrierSectionHeader>
              ) : null}

              {renderMainGrid?.(carriersWithoutSubgroup, { parentGroupName })}

              {subgroups?.length > 0 && (
                <>
                  <Separator variant='dashed' />
                  {subgroups.map(({ groupName, carriers: subgroupCarriers }) => (
                    <div key={groupName} className='flex flex-col gap-3'>
                      <h4 className='m-0 text-left text-lg font-semibold text-gray-700'>
                        {groupName}
                      </h4>
                      {renderSubgroupGrid?.(subgroupCarriers, {
                        parentGroupName,
                        groupName
                      })}
                    </div>
                  ))}
                </>
              )}
            </section>
          );
        }
      )}
    </div>
  );
}

GroupedCarrierSections.propTypes = {
  groupedCarriers: groupedCarriersShape,
  subtitle: propTypes.string,
  wrapperClassName: propTypes.string,
  sectionClassName: propTypes.string,
  sectionHeaderClassName: propTypes.string,
  filterLegendsForGrid: propTypes.bool,
  renderHeaderChildren: propTypes.func,
  renderMainGrid: propTypes.func.isRequired,
  renderSubgroupGrid: propTypes.func
};
